<template>
  <div>
    <div
      class="catalog-sidebar z-index__32"
      :class="{ active: showModalMobile }"
    >
      <div
        class="catalog-sidebar__header show-until-desktop"
        :class="{
          'catalog-sidebar__header--border': styleBorder.top
        }"
      >
        {{ $t('catalog.filters.title_mobile') }}
        <span v-if="selectedFilterCount > 0" class="catalog-sidebar__count">
          {{ selectedFilterCount }}
        </span>

        <div class="modal-body__close icon-times-light" @click="handleHideMobileModal" />
      </div>

      <ProductsFiltersPreload v-if="fetchingContent" />

      <template v-else>
        <div
          class="filters-wrapper"
          @scroll="handleScrollAnimation"
          @click="handleChangeScroll"
        >
          <div class="show-until-desktop">
            <slot />
          </div>

          <div v-if="hasFilters" class="filters">
            <BaseAccordion
              v-for="filterGroup in filteredFiltersGroups"
              ref="filtersElementRefs"
              :key="filterGroup.code"
              :data-code="filterGroup.code"
              class="filters__block"
              :title="getFilterTitle(filterGroup)"
              :is-opened="getShowed(filterGroup.code)"
              @change="handleChangeAccordion(filterGroup.code, $event)"
            >
              <template
                v-for="filterItem in filterGroup.items"
                :key="filterItem.code"
              >
                <component
                  :is="getFilterComponentName(filterItem)"
                  :filter="filterItem"
                  :show-more="getShowedMore(filterItem.code)"
                  :selected="getSelectedFilters(filterItem)"
                  :limit="getFilterLimit(filterItem.code)"
                  @on-change="proxyChangeFilterQuery($event, filterGroup)"
                  @on-show="onViewFilter($event, filterGroup)"
                  @show-more="handleChangeShowMore(filterItem.code, $event)"
                />
              </template>
            </BaseAccordion>

            <div
              v-show="fetching"
              class="show-from-desktop filters__loader z-index__1"
            >
              <BaseLoader />
            </div>
          </div>
        </div>
      </template>

      <div
        class="catalog-sidebar__buttons show-until-desktop z-index__1"
        :class="{
          'catalog-sidebar__buttons--border': styleBorder.bottom
        }"
      >
        <BaseButton
          size="small"
          :loading="fetching"
          full-width
          outline
          @click="handleSubmit"
        >
          {{
            $t('catalog.filters.apply', {
              count: countFormated
            })
          }}
        </BaseButton>
      </div>
    </div>

    <div
      v-show="showModalMobile"
      ref="modal-mask-ref"
      class="catalog-sidebar-mask z-index__25 show-until-desktop"
      @click="handleHideMobileModal"
    />
  </div>
</template>

<script setup lang="ts">
import {
  BONUSES_FILTER_CODE,
  BRAND_GROUP_FILTER_CODE,
  CITY_FILTER_CODE,
  CONDITION_FILTER_CODE,
  FREE_DELIVERY_FILTER_CODE,
  IN_STOCK_FILTER_CODE,
  PRICE_FILTER_CODE,
  PRICE_GROUP_FILTER_CODE,
  SALE_FILTER_CODE
} from '@/constants/common'
import type { CatalogFilter, CatalogFilterWrap, FilterItemChangePayload } from '~/types/catalog'

const FilterCheckbox = resolveComponent('FilterCheckbox')
const FilterTile = resolveComponent('FilterTile')
const FilterSlider = resolveComponent('FilterSlider')
const FilterBool = resolveComponent('FilterBool')
const FilterCity = resolveComponent('FilterCity')
const FilterRangeInput = resolveComponent('FilterRangeInput')

const filterComponents: Record<string, ReturnType<typeof resolveComponent>> = {
  FilterCheckbox,
  FilterTile,
  FilterSlider,
  FilterBool,
  FilterCity,
  FilterRangeInput
}

interface FilterChangeAnaliticsData {
  filterName: string
  filterCode: string
  filterValueName?: string
  filterValueCode?: string
}

type Props = {
  showModalMobile: boolean
  selectedFilterCount: number
  showBonusesFilter: boolean
  showInStockFilter: boolean
  hideCondition?: boolean
}
type Emits = {
  (e: 'change', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const route = useRoute()
const i18n = useI18n()
const { $device } = useNuxtApp()
const commonMetricsStore = useCommonMetricsStore()
const catalogFiltersStore = useCatalogFiltersStore()
const {
  stopScrollSubscribe,
  stopScrollUnsubscribe
} = useOverflowed()

const styleBorder = shallowReactive({
  top: false,
  bottom: false
})

const {
  handleScrollAnimation
} = useScrollElement(({ top, bottom, el }) => {
  styleBorder.top = top > 0
  styleBorder.bottom = bottom !== el.scrollHeight
})

const handleChangeScroll = (ev: Event) => {
  if ($device.isDesktop) {
    return
  }

  const targetElement = ev.currentTarget as HTMLElement
  if (!targetElement) return

  nextTick(() => {
    styleBorder.bottom = targetElement.scrollHeight > targetElement.offsetHeight
  })
}

const modalMask = useTemplateRef<HTMLDivElement>('modal-mask-ref')
const filtersElements = useTemplateRef('filtersElementRefs')

const {
  fetching,
  fetchingContent,
  filterQuery,
  showedFilters,
  latestPositionFilter,
  countFormated,
  filters
} = storeToRefs(catalogFiltersStore)

const hasFilters = computed(() => {
  return filters.value.length > 0
})
const isSellerPage = computed(() => {
  return checkRoutePage(unref(route), 'seller')
})

const filteredFiltersGroups = computed<CatalogFilterWrap[]>(() => {
  const filteredFilters: CatalogFilterWrap[] = []

  for (const filter of filters.value) {
    if (filter.code === PRICE_GROUP_FILTER_CODE) {
      const items = filter.items.filter((item: CatalogFilter) => {
        // вернути усі вкладені фільтри
        // не повертати "Купити за бонуси" на сторінці бонусних пропозицій
        return (props.showBonusesFilter && item.code === BONUSES_FILTER_CODE) ||
          (props.showInStockFilter && item.code === IN_STOCK_FILTER_CODE) ||
          (item.code === SALE_FILTER_CODE) ||
          [FREE_DELIVERY_FILTER_CODE, PRICE_FILTER_CODE].includes(item.code)
      })

      filteredFilters.push({
        ...filter,
        items
      })
    } else if (
      (props.hideCondition && filter.code === CONDITION_FILTER_CODE) ||
      (isSellerPage.value && filter.code === CITY_FILTER_CODE)
    ) {
      // console.log('filter', filter)
    } else {
      filteredFilters.push(filter)
    }
    // Фільтруємо значення фільтра "Brand" щоб не було брендів із кількістю 0 якщо вибрана категорія
    // else if (filter.code == BRAND_GROUP_FILTER_CODE && Boolean(this.category)) {
    //   filters.push({
    //     ...filter,
    //     items: [{
    //       ...filter.items[0],
    //       items: filter.items[0].items.filter(item => item.count > 0)
    //     }]
    //   })
    //  continue
    // }
  }

  // Return group where filter items has more 1 item
  return filteredFilters.filter((item) => {
    if (!item.items.length) {
      return false
    }

    const firstItem = item.items[0]

    return !['checkbox', 'tile'].includes(firstItem.type) || firstItem.items.length > 1
  })
})

const getFilterLimit = (code: string) => {
  return code === BRAND_GROUP_FILTER_CODE ? 7 : undefined
}

const getShowedMore = (code: string) => {
  return Boolean(showedFilters.value[code]?.showMore)
}

const getFilterComponentName = (filterItem: CatalogFilter) => {
  const type: string = filterItem.code === CITY_FILTER_CODE
    ? 'city'
    : filterItem.type === 'custom_range_filter'
      ? 'checkbox'
      : filterItem.type
  const normalisedName: string = type
    .split('_')
    .map((str) => uppertFirst(str))
    .join('')
  const componentName = 'Filter' + normalisedName

  return filterComponents[componentName]
}

const getFilterTitle = (filter: CatalogFilterWrap) => {
  if (filter.code === CITY_FILTER_CODE) {
    return i18n.t('catalog.filters.search_for_city') as string
  }
  const firstFilter: CatalogFilter | undefined = filter.items[0]
  const isTypeRange = firstFilter?.type === 'range_input'

  if (isTypeRange && firstFilter.unit) {
    return `${firstFilter.name} (${firstFilter.unit})`
  }
  return uppertFirst(filter.title)
}

const getSelectedFilters = (filterItem: CatalogFilter) => {
  const code = filterItem.code === PRICE_FILTER_CODE
    ? filterItem.uuid
    : filterItem.code

  return filterQuery.value[code] || ''
}

const handleChangeAccordion = (code: string, accordion: boolean) => {
  catalogFiltersStore.changeShowedFilters({
    code,
    accordion
  })
}

const handleChangeShowMore = (code: string, showMore: boolean) => {
  catalogFiltersStore.changeShowedFilters({
    code,
    showMore
  })
}

const getShowed = (code: string) => {
  if (showedFilters.value[code]) {
    return showedFilters.value[code]!.accordion
  } else {
    return !$device.isMobile
      // https://spvtechs.myjetbrains.com/youtrack/issue/SNT-7019
      // Згортаємо усі фільтри на мобілі
      // ? false
      // ? index === 0 ||
      //     [CITY_FILTER_CODE, PRICE_GROUP_FILTER_CODE].includes(code)
      // : true
  }
}

const getFilterEventPayload = (data: FilterItemChangePayload, filterGroup: CatalogFilterWrap): FilterChangeAnaliticsData => ({
  filterName: filterGroup.title,
  filterCode: filterGroup.code,
  filterValueName: data.label,
  filterValueCode: data.value
})

const proxyChangeFilterQuery = (data: FilterItemChangePayload, filterGroup: CatalogFilterWrap) => {
  try {
    const position = $device.isDesktop
      ? filtersElements.value?.find((item) =>
          item && item.$el.getAttribute('data-code') === data.filter_code
        )?.$el.getBoundingClientRect().top
      : undefined

    catalogFiltersStore.changeFilterQuery(data, position)
  } catch (e) {
    console.error(e)
  }

  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-filter]',
    event_type: 'click',
    event_content: getFilterEventPayload(data, filterGroup)
  })
}

const onViewFilter = (data: FilterItemChangePayload, filterGroup: CatalogFilterWrap) => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-filter]',
    event_type: 'view',
    event_content: getFilterEventPayload(data, filterGroup)
  })
}

const handleHideMobileModal = () => emit('change', false)
const handleSubmit = () => {
  handleHideMobileModal()
  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-filter-submit]',
    event_type: 'click',
    event_content: ''
  })
}

const scrollToLastFilter = () => {
  if (!$device.isDesktop || !latestPositionFilter.value || fetching.value && props.selectedFilterCount) {
    return
  }

  const latestFilter = latestPositionFilter.value
  const element = filtersElements.value?.find((item) => {
    return item && item.$el.getAttribute('data-code') === latestFilter!.code
  })

  if (element) {
    const elementTop = element.$el.getBoundingClientRect().top
    const elementLatestTop = latestFilter!.top

    if (elementTop !== elementLatestTop) {
      window.scrollTo({ top: scrollY + (elementTop - elementLatestTop) })
    }
  }

  catalogFiltersStore.deleteLatestPositionFilter()
}

const stopScrollUnsubscribeMask = () => {
  stopScrollUnsubscribe(modalMask.value!)
}

watch(() => props.showModalMobile, (show: boolean) => {
  if (show) {
    stopScrollSubscribe(modalMask.value!)
  } else {
    stopScrollUnsubscribeMask()
  }
})

// START SNT-6076
// Десктоп. При натисканні фільтра лишати екран так, щоб фільтр лишався в тому ж місці.
watch(fetching, () => {
  if (import.meta.client) {
    nextTick(scrollToLastFilter)
  }
}, { flush: 'post' })
// END SNT-6076

onMounted(scrollToLastFilter)
onBeforeUnmount(stopScrollUnsubscribeMask)
</script>

<style lang="scss" scoped>
.filters-wrapper {
  @media only screen and (max-width: 1024px) {
    overflow: auto;
    height: calc(100% - #{5.8+7.6+rem});
    margin: 0;
    padding: .5rem 1.6rem 0;
  }

  @media only screen and (max-width: 740px) {
    height: calc(100% - #{5.8+7.2+rem});
  }
}

.catalog-sidebar {
  &__header {
    transition: box-shadow .2s;
    &--border {
      box-shadow: 0 1px 2px #e5e5e5;
    }
  }
  &__buttons {
    transition: box-shadow .2s;
    &--border {
      box-shadow: 0 -1px 2px #e5e5e5;
    }
  }
}

.filters {
  position: relative;

  &__title {
    padding-bottom: 2.6rem;
    font-size: $h6-font-size;
  }

  &__block {
    padding-top: 1.6rem;
    padding-bottom: 1.6rem;
    border-top: solid 1px #e5e5e5;

    @media only screen and (min-width: 740px) {
      padding-top: 1.4rem;
      padding-bottom: 1.4rem;
    }

    &:first-child {
      border: none;

      @media only screen and (min-width: 740px) {
        padding-top: 0;
      }
    }
  }

  &__loader {
    top: 0;
    bottom: 0;
    position: absolute;
    background: rgba(255, 255, 255, 0.5);
    left: 0;
    right: 0;
  }
}

.modal-body {
  &__close {
    position: absolute;
    top: 1rem;
    right: 1.2rem;

    @media only screen and (min-width: 740px) {
      top: 1.4rem;
      font-size: 2.4rem;
    }
  }
}
</style>
